import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { formatDateTime } from '../utils/dateUtils';
import { generateICSLink } from '../events/eventActions';
import { getGoogleCalendarUrl } from '../utils/calendarUtils';
import DOMPurify from 'dompurify';

const EMAIL_COLLECTION = 'EMAIL_COLLECTION';

const EMAIL_TEMPLATES = {
  WELCOME: 'jy7zpl90yy3l5vx6',
  EVENT_DETAIL: '3z0vklormq1l7qrx'
};

/**
 * Format HTML for email with proper blockquote handling and clean formatting
 */
const formatHtmlForEmail = (html) => {
  if (!html) return '';
  
  // First, remove empty paragraphs and divs
  let text = html.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, '');
  text = text.replace(/<div>(&nbsp;|\s|<br\s*\/?>)*<\/div>/gi, '');
  
  // Handle blockquotes with email-friendly styling - BEFORE other processing
  text = text.replace(/<blockquote>([\s\S]*?)<\/blockquote>/gi, (match, content) => {
    // First, convert any tags inside to proper format
    let cleanContent = content
      .replace(/<h[1-6]>(.*?)<\/h[1-6]>/gi, '$1') // Strip heading tags but keep content
      .replace(/<strong>(.*?)<\/strong>/gi, '<b>$1</b>') // Convert strong to b
      .replace(/<em>(.*?)<\/em>/gi, '<i>$1</i>') // Convert em to i
      .replace(/<br\s*\/?>/gi, ' ') // Convert breaks to spaces
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .trim()
      .replace(/^["'\s]+|["'\s]+$/g, ''); // Trim quotes
    
    return cleanContent 
      ? `<p style="margin: 16px 0; padding: 12px 16px; border-left: 4px solid #3B82F6; background-color: #F3F4F6; font-style: italic; color: #374151;">"${cleanContent}"</p>`
      : '';
  });
  
  // Style headings with inline styles
  text = text.replace(/<h1>/gi, '<h1 style="margin: 20px 0 12px 0; font-size: 24px; font-weight: bold; color: #111827;">');
  text = text.replace(/<h2>/gi, '<h2 style="margin: 18px 0 10px 0; font-size: 20px; font-weight: bold; color: #111827;">');
  text = text.replace(/<h3>/gi, '<h3 style="margin: 16px 0 8px 0; font-size: 18px; font-weight: bold; color: #111827;">');
  text = text.replace(/<h4>/gi, '<h4 style="margin: 14px 0 8px 0; font-size: 16px; font-weight: bold; color: #111827;">');
  
  // Ensure paragraphs have proper spacing
  text = text.replace(/<p>/gi, '<p style="margin: 12px 0; line-height: 1.6; color: #374151;">');
  
  // Convert strong/em to b/i for better email compatibility
  text = text.replace(/<strong>/gi, '<b>');
  text = text.replace(/<\/strong>/gi, '</b>');
  text = text.replace(/<em>/gi, '<i>');
  text = text.replace(/<\/em>/gi, '</i>');
  
  // Style lists
  text = text.replace(/<ul>/gi, '<ul style="margin: 12px 0; padding-left: 24px; line-height: 1.6;">');
  text = text.replace(/<ol>/gi, '<ol style="margin: 12px 0; padding-left: 24px; line-height: 1.6;">');
  text = text.replace(/<li>/gi, '<li style="margin: 6px 0; color: #374151;">');
  
  // Convert links to plain URLs (email clients will auto-link them)
  text = text.replace(/<a\s+[^>]*?href=["']([^"']+)["'][^>]*?>(.*?)<\/a>/gi, (match, url, linkText) => {
    // If link text is different from URL, show both
    if (linkText.trim() && linkText.trim() !== url) {
      return `${linkText} (${url})`;
    }
    // Otherwise just show the URL
    return url;
  });
  
  // Clean up excessive breaks
  text = text.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
  
  // Remove leading/trailing breaks and whitespace
  text = text.replace(/^(<br\s*\/?>|&nbsp;|\s)+|(<br\s*\/?>|&nbsp;|\s)+$/gi, '');
  
  return text;
};

export async function sendWelcomeEmail(recipientEmail, recipientName) {
  console.log('sendWelcomeEmail called for:', recipientEmail);
  
  const emailData = {
    to: [
      {
        email: recipientEmail,
        name: recipientName || 'Friend'  // Provide a default name if not provided
      }
    ],
    from: {
      email: 'no-reply@gatherly.org',
      name: 'Gatherly'
    },
    subject: 'Welcome to Gatherly!',
    template_id: EMAIL_TEMPLATES.WELCOME,
    personalization: [
      {
        email: recipientEmail,
        data: {
          name: recipientName || 'Friend',
          company: 'Gatherly'
        }
      }
    ]
  };

  console.log('Welcome email data being sent:', JSON.stringify(emailData, null, 2));

  return sendEmail(emailData);
}

export async function sendEventDetailEmail(recipientEmail, recipientName, event, isRSVP = false, isUpdate = false, rsvpStatus = 'yes', rsvpRemoved = false, removedByHost = false) {
  console.log('Sending event detail email:', { recipientEmail, recipientName, event, isRSVP, isUpdate, rsvpStatus, rsvpRemoved, removedByHost });

  const formattedDate = formatDateTime(event.startDate, event.startTime, event.endDate, event.endTime, event.timezone);
  const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`;

  // Generate calendar links
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://gatherly.org';
  const eventUrl = `${origin}/event/${event.url}`;
  const icsFileLink = `${origin}/event/${event.url}/ics`;

  // Generate Google Calendar link using the same function as the website
  const googleCalendarLink = getGoogleCalendarUrl(event, eventUrl);

  // Format the description with proper HTML for email
  const formattedDescription = formatHtmlForEmail(event.description);

  let subject;
  if (rsvpRemoved) {
    subject = removedByHost ? `Host removed your RSVP to ${event.name}` : `You removed your RSVP to ${event.name}`;
  } else if (isUpdate) {
    subject = `${event.name} has been updated`;
  } else if (isRSVP) {
    // Same subjects as when first RSVPing
    if (rsvpStatus === 'yes') {
      subject = `You're going to ${event.name}!`;
    } else if (rsvpStatus === 'maybe') {
      subject = `You're a maybe for ${event.name}`;
    } else if (rsvpStatus === 'no') {
      subject = `You can't make it to ${event.name}`;
    } else {
      subject = `You're going to ${event.name}!`; // default
    }
  } else {
    subject = `${event.name}`;
  }

  const emailData = {
    to: [
      {
        email: recipientEmail,
        name: recipientName || 'Gatherly User'  // Provide a default name if not provided
      }
    ],
    from: {
      email: 'no-reply@gatherly.org',
      name: 'Gatherly'
    },
    subject: subject,
    template_id: EMAIL_TEMPLATES.EVENT_DETAIL,
    personalization: [
      {
        email: recipientEmail,
        data: {
          recipient_name: recipientName || 'Gatherly User',
          event_name: event.name,
          event_url: eventUrl,
          event_date: formattedDate,
          event_location: event.location || '',
          event_location_link: googleMapsLink,
          event_description: formattedDescription,
          host_name: event.hostName || 'The Gatherly Team',
          host_email: event.hostEmail || 'support@gatherly.org',
          google_calendar_link: googleCalendarLink,
          ics_file_link: icsFileLink
        }
      }
    ]
  };

  console.log('Event detail email data being sent:', JSON.stringify(emailData, null, 2));

  return sendEmail(emailData);
}

/**
 * Send a notification email to the event host when someone RSVPs or updates their RSVP
 * Uses plain HTML email (no template needed - works with free MailerSend)
 */
export async function sendHostRSVPNotification(hostEmail, hostName, event, rsvpData, allRsvps = [], isUpdate = false, oldStatus = null) {
  console.log('Sending host RSVP notification:', { hostEmail, hostName, event, rsvpData, isUpdate, oldStatus });

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://gatherly.org';
  const eventUrl = `${origin}/event/${event.url}`;
  const formattedDate = formatDateTime(event.startDate, event.startTime, event.endDate, event.endTime, event.timezone);

  // Format RSVP status for display
  const statusLabels = {
    yes: 'Attending',
    maybe: 'Maybe',
    no: "Can't Make It"
  };
  const statusLabel = statusLabels[rsvpData.status] || 'Attending';
  const oldStatusLabel = oldStatus ? statusLabels[oldStatus] || 'Attending' : null;

  // Build RSVP summary counts
  const yesCount = allRsvps.filter(r => r.status === 'yes').reduce((acc, r) => acc + 1 + (r.guestCount || 0), 0);
  const maybeCount = allRsvps.filter(r => r.status === 'maybe').reduce((acc, r) => acc + 1 + (r.guestCount || 0), 0);
  const noCount = allRsvps.filter(r => r.status === 'no').reduce((acc, r) => acc + 1 + (r.guestCount || 0), 0);

  // Format guest count text
  const guestText = rsvpData.guestCount > 0 ? ` + ${rsvpData.guestCount} guest${rsvpData.guestCount > 1 ? 's' : ''}` : '';

  // Build RSVP list (up to 50, sorted by status then name)
  const sortedRsvps = [...allRsvps]
    .sort((a, b) => {
      // Sort order: yes, maybe, no
      const statusOrder = { yes: 1, maybe: 2, no: 3 };
      const aOrder = statusOrder[a.status] || 999;
      const bOrder = statusOrder[b.status] || 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.name || '').localeCompare(b.name || '');
    })
    .slice(0, 50);

  const rsvpListHtml = sortedRsvps.map(r => {
    const guestCount = r.guestCount > 0 ? ` +${r.guestCount}` : '';
    const statusColor = r.status === 'yes' ? '#059669' : r.status === 'maybe' ? '#6B7280' : '#DC2626';
    const label = statusLabels[r.status] || 'Attending';
    const comment = r.comment ? ` <span style="font-style: italic; color: #6B7280; font-size: 13px;">  "${r.comment}"</span>` : '';
    return `
      <tr>
        <td style="padding: 8px 0; border-bottom: 1px solid #F3F4F6;">
          <strong>${r.name || 'Guest'}</strong>${guestCount}${comment}
        </td>
        <td style="padding: 8px 0; border-bottom: 1px solid #F3F4F6; text-align: right; vertical-align: top;">
          <span style="color: ${statusColor}; font-size: 14px;">${label}</span>
        </td>
      </tr>
    `;
  }).join('');

  const totalRsvps = allRsvps.length;
  const moreRsvps = totalRsvps > 50 ? `<p style="margin: 8px 0 0 0; font-size: 14px; color: #6B7280; font-style: italic;">+ ${totalRsvps - 50} more</p>` : '';

  // Format the timestamp
  const timestamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  // Email action text
  const actionText = isUpdate ? 'updated their RSVP' : 'just RSVP\'d';
  const headerText = isUpdate ? `RSVP Updated for ${event.name}` : `New RSVP for ${event.name}`;
  
  // Dynamic header color based on status
  const headerColor = rsvpData.status === 'yes' ? '#059669' : rsvpData.status === 'maybe' ? '#6B7280' : '#DC2626';
  const borderColor = headerColor;
  
  // Color for current status
  const statusColor = headerColor;
  
  // Color for old status (if it exists)
  const oldStatusColor = oldStatus === 'yes' ? '#059669' : oldStatus === 'maybe' ? '#6B7280' : '#DC2626';
  
  // Build the details line with color-coded statuses
  let detailsLine;
  if (isUpdate && oldStatusLabel) {
    detailsLine = `From <strong style="color: ${oldStatusColor};">${oldStatusLabel}</strong> to <strong style="color: ${statusColor};">${statusLabel}</strong> on ${timestamp}`;
  } else {
    detailsLine = `<strong style="color: ${statusColor};">${statusLabel}</strong> as of ${timestamp}`;
  }

  // Build HTML email
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #374151; line-height: 1.6; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background-color: ${headerColor}; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
    .content { background-color: #F9FAFB; padding: 24px; border: 1px solid #E5E7EB; border-top: none; }
    .rsvp-box { background-color: white; padding: 16px; border-radius: 8px; margin: 16px 0; border-left: 4px solid ${borderColor}; }
    .summary { background-color: white; padding: 16px; border-radius: 8px; margin: 16px 0; }
    .rsvp-list { background-color: white; padding: 16px; border-radius: 8px; margin: 16px 0; }
    .button { display: inline-block; background-color: #3B82F6; color: white !important; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 16px 0; font-weight: 600; }
    .footer { text-align: center; color: #6B7280; font-size: 14px; margin-top: 20px; }
    table { width: 100%; border-collapse: collapse; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">${headerText}</h1>
    </div>
    <div class="content">
      <div class="rsvp-box">
        <p style="margin: 0 0 8px 0; font-size: 16px;"><strong>${rsvpData.name || 'Someone'}</strong> ${actionText}${guestText}</p>
        <p style="margin: 0 0 8px 0; font-size: 14px; color: #6B7280;">
          ${detailsLine}${rsvpData.comment ? ' with the following note:' : ''}
        </p>
        ${rsvpData.comment ? `<p style="margin: 0 0 8px 0; font-size: 14px; font-style: italic; color: #6B7280;">"${rsvpData.comment}"</p>` : ''}
        <p style="margin: 0; font-size: 14px; color: #6B7280;">
          Contact: ${rsvpData.email}
        </p>
      </div>

      <div class="summary">
        <p style="margin: 0 0 12px 0; font-weight: 600; color: #111827;">Current RSVP Summary</p>
        <p style="margin: 4px 0;"><span style="color: #059669;">●</span> <strong>Attending:</strong> ${yesCount}</p>
        <p style="margin: 4px 0;"><span style="color: #6B7280;">●</span> <strong>Maybe:</strong> ${maybeCount}</p>
        <p style="margin: 4px 0;"><span style="color: #DC2626;">●</span> <strong>Can't Make It:</strong> ${noCount}</p>
        <p style="margin: 16px 0 4px 0; font-size: 14px; color: #6B7280;">
          <strong>Event:</strong> ${event.name}<br>
          <strong>When:</strong> ${formattedDate}<br>
          <strong>Where:</strong> ${event.location}
        </p>
      </div>

      <div class="rsvp-list">
        <p style="margin: 0 0 12px 0; font-weight: 600; color: #111827;">All RSVPs</p>
        <table>
          ${rsvpListHtml}
        </table>
        ${moreRsvps}
      </div>

      <a href="${eventUrl}" class="button" style="color: white;">View Event & Manage RSVPs</a>

      <p style="margin: 16px 0 0 0; font-size: 14px; color: #6B7280;">
        You're receiving this because you're the host of this event. You can view all RSVPs and manage your event at any time.
      </p>
    </div>
    <div class="footer">
      <p>Gatherly - The simple way to organize gatherings</p>
    </div>
  </div>
</body>
</html>
  `;

  // Format status for subject line
  const statusPhrase = rsvpData.status === 'yes' ? 'is coming' : rsvpData.status === 'maybe' ? 'might come' : "can't make it";

  const emailData = {
    to: [
      {
        email: hostEmail,
        name: hostName || 'Event Host'
      }
    ],
    from: {
      email: 'no-reply@gatherly.org',
      name: 'Gatherly'
    },
    subject: isUpdate && oldStatusLabel
      ? `${rsvpData.name || 'Someone'} changed their RSVP for ${event.name}`
      : `${rsvpData.name || 'Someone'} ${statusPhrase} to ${event.name}`,
    html: html
  };

  console.log('Host RSVP notification email data:', JSON.stringify(emailData, null, 2));

  return sendEmail(emailData);
}

/**
 * Send a notification email to the event host when someone removes their RSVP
 * Uses plain HTML email (no template needed - works with free MailerSend)
 */
export async function sendHostRSVPRemovalNotification(hostEmail, hostName, event, rsvpData, allRsvps = []) {
  console.log('Sending host RSVP removal notification:', { hostEmail, hostName, event, rsvpData });

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://gatherly.org';
  const eventUrl = `${origin}/event/${event.url}`;
  const formattedDate = formatDateTime(event.startDate, event.startTime, event.endDate, event.endTime, event.timezone);

  // Format RSVP status for display
  const statusLabels = {
    yes: 'Attending',
    maybe: 'Maybe',
    no: "Can't Make It"
  };
  const previousStatusLabel = statusLabels[rsvpData.status] || 'Attending';
  
  // Color for previous status
  const previousStatusColor = rsvpData.status === 'yes' ? '#059669' : rsvpData.status === 'maybe' ? '#6B7280' : '#DC2626';

  // Build RSVP summary counts
  const yesCount = allRsvps.filter(r => r.status === 'yes').reduce((acc, r) => acc + 1 + (r.guestCount || 0), 0);
  const maybeCount = allRsvps.filter(r => r.status === 'maybe').reduce((acc, r) => acc + 1 + (r.guestCount || 0), 0);
  const noCount = allRsvps.filter(r => r.status === 'no').reduce((acc, r) => acc + 1 + (r.guestCount || 0), 0);

  // Format guest count text
  const guestText = rsvpData.guestCount > 0 ? ` + ${rsvpData.guestCount} guest${rsvpData.guestCount > 1 ? 's' : ''}` : '';

  // Build RSVP list (up to 50, sorted by status then name)
  const sortedRsvps = [...allRsvps]
    .sort((a, b) => {
      // Sort order: yes, maybe, no
      const statusOrder = { yes: 1, maybe: 2, no: 3 };
      const aOrder = statusOrder[a.status] || 999;
      const bOrder = statusOrder[b.status] || 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.name || '').localeCompare(b.name || '');
    })
    .slice(0, 50);

  const rsvpListHtml = sortedRsvps.map(r => {
    const guestCount = r.guestCount > 0 ? ` +${r.guestCount}` : '';
    const statusColor = r.status === 'yes' ? '#059669' : r.status === 'maybe' ? '#6B7280' : '#DC2626';
    const label = statusLabels[r.status] || 'Attending';
    const comment = r.comment ? ` <span style="font-style: italic; color: #6B7280; font-size: 13px;">  "${r.comment}"</span>` : '';
    return `
      <tr>
        <td style="padding: 8px 0; border-bottom: 1px solid #F3F4F6;">
          <strong>${r.name || 'Guest'}</strong>${guestCount}${comment}
        </td>
        <td style="padding: 8px 0; border-bottom: 1px solid #F3F4F6; text-align: right; vertical-align: top;">
          <span style="color: ${statusColor}; font-size: 14px;">${label}</span>
        </td>
      </tr>
    `;
  }).join('');

  const totalRsvps = allRsvps.length;
  const moreRsvps = totalRsvps > 50 ? `<p style="margin: 8px 0 0 0; font-size: 14px; color: #6B7280; font-style: italic;">+ ${totalRsvps - 50} more</p>` : '';

  // Format the timestamp
  const timestamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  // Build HTML email
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #374151; line-height: 1.6; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background-color: #DC2626; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
    .content { background-color: #F9FAFB; padding: 24px; border: 1px solid #E5E7EB; border-top: none; }
    .rsvp-box { background-color: white; padding: 16px; border-radius: 8px; margin: 16px 0; border-left: 4px solid #DC2626; }
    .summary { background-color: white; padding: 16px; border-radius: 8px; margin: 16px 0; }
    .rsvp-list { background-color: white; padding: 16px; border-radius: 8px; margin: 16px 0; }
    .button { display: inline-block; background-color: #3B82F6; color: white !important; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 16px 0; font-weight: 600; }
    .footer { text-align: center; color: #6B7280; font-size: 14px; margin-top: 20px; }
    table { width: 100%; border-collapse: collapse; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">RSVP Removed for ${event.name}</h1>
    </div>
    <div class="content">
      <div class="rsvp-box">
        <p style="margin: 0 0 8px 0; font-size: 16px;"><strong>${rsvpData.name || 'Someone'}</strong> removed their RSVP${guestText}</p>
        <p style="margin: 0 0 8px 0; font-size: 14px; color: #6B7280;">
          Previously <strong style="color: ${previousStatusColor};">${previousStatusLabel}</strong>, removed on ${timestamp}${rsvpData.comment ? ' with this note:' : ''}
        </p>
        ${rsvpData.comment ? `<p style="margin: 0 0 8px 0; font-size: 14px; font-style: italic; color: #6B7280;">"${rsvpData.comment}"</p>` : ''}
        <p style="margin: 0; font-size: 14px; color: #6B7280;">
          Contact: ${rsvpData.email}
        </p>
      </div>

      <div class="summary">
        <p style="margin: 0 0 12px 0; font-weight: 600; color: #111827;">Current RSVP Summary</p>
        <p style="margin: 4px 0;"><span style="color: #059669;">●</span> <strong>Attending:</strong> ${yesCount}</p>
        <p style="margin: 4px 0;"><span style="color: #6B7280;">●</span> <strong>Maybe:</strong> ${maybeCount}</p>
        <p style="margin: 4px 0;"><span style="color: #DC2626;">●</span> <strong>Can't Make It:</strong> ${noCount}</p>
        <p style="margin: 16px 0 4px 0; font-size: 14px; color: #6B7280;">
          <strong>Event:</strong> ${event.name}<br>
          <strong>When:</strong> ${formattedDate}<br>
          <strong>Where:</strong> ${event.location}
        </p>
      </div>

      <div class="rsvp-list">
        <p style="margin: 0 0 12px 0; font-weight: 600; color: #111827;">All RSVPs (${totalRsvps})</p>
        <table>
          ${rsvpListHtml}
        </table>
        ${moreRsvps}
      </div>

      <a href="${eventUrl}" class="button" style="color: white;">View Event & Manage RSVPs</a>

      <p style="margin: 16px 0 0 0; font-size: 14px; color: #6B7280;">
        You're receiving this because you're the host of this event. You can view all RSVPs and manage your event at any time.
      </p>
    </div>
    <div class="footer">
      <p>Gatherly - The simple way to organize gatherings</p>
    </div>
  </div>
</body>
</html>
  `;

  const emailData = {
    to: [
      {
        email: hostEmail,
        name: hostName || 'Event Host'
      }
    ],
    from: {
      email: 'no-reply@gatherly.org',
      name: 'Gatherly'
    },
    subject: `${rsvpData.name || 'Someone'} removed their RSVP for ${event.name}`,
    html: html
  };

  console.log('Host RSVP removal notification email data:', JSON.stringify(emailData, null, 2));

  return sendEmail(emailData);
}

export async function sendEmail(emailData) {
  console.log('Attempting to send email:', emailData);
  try {
    const docRef = await addDoc(collection(db, EMAIL_COLLECTION), emailData);
    console.log('Email document written with ID: ', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error sending email: ', error);
    console.log('Error details:', error.code, error.message);
    if (error.message.includes('must be one of the verified domains')) {
      console.log('Development mode: Simulating email send');
      return { 
        success: true, 
        id: 'dev-mode-' + Date.now(),
        message: 'Email simulated in development mode'
      };
    }
    return { success: false, error: error.message };
  }
}

/**
 * Send authentication code email
 * Uses plain HTML email (no template needed - works with free MailerSend)
 */
export async function sendAuthCodeEmail(recipientEmail, recipientName, code, isSignUp = false, eventId = null) {
  console.log('Sending auth code email:', { recipientEmail, recipientName, code, isSignUp, eventId });

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://gatherly.org';
  let loginUrl = `${origin}/login?code=${code}&email=${encodeURIComponent(recipientEmail)}`;
  if (eventId) {
    loginUrl += `&rsvp=true&eventId=${eventId}`;
  }

  // Clean, modern HTML aligned with app (gray/white, sky blue accent, no emoji)
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #374151; line-height: 1.6; margin: 0; padding: 0; background-color: #f5f5f4; }
    .wrap { max-width: 480px; margin: 0 auto; padding: 24px 16px; }
    .card { background: #fff; border: 1px solid #e7e5e4; border-radius: 12px; overflow: hidden; }
    .head { background: #fafaf9; padding: 20px 24px; border-bottom: 1px solid #e7e5e4; }
    .head h1 { margin: 0; font-size: 20px; font-weight: 600; color: #1c1917; }
    .body { padding: 24px; }
    .body p { margin: 0 0 12px 0; font-size: 15px; color: #44403c; }
    .code-wrap { background: #fafaf9; border: 1px solid #e7e5e4; border-radius: 8px; padding: 16px 20px; margin: 20px 0; text-align: center; }
    .code { font-size: 26px; font-weight: 600; letter-spacing: 6px; color: #1c1917; font-family: 'Courier New', monospace; margin: 4px 0; }
    .code-meta { font-size: 12px; color: #78716c; margin-top: 6px; }
    .cta { text-align: center; margin: 20px 0 16px 0; }
    .btn { display: inline-block; background: #0284c7; color: #fff !important; padding: 12px 24px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 15px; }
    .muted { font-size: 13px; color: #78716c; line-height: 1.5; margin-top: 20px; }
    .foot { text-align: center; font-size: 13px; color: #78716c; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e7e5e4; }
    .foot a { color: #0284c7; text-decoration: none; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="card">
      <div class="head">
        <h1>Sign in to Gatherly</h1>
      </div>
      <div class="body">
        <p>Hi${recipientName ? ' ' + recipientName : ' there'},</p>
        <p>Use this code to sign in:</p>
        <div class="code-wrap">
          <div class="code">${code}</div>
          <div class="code-meta">Valid for 15 minutes</div>
        </div>
        <p class="muted">Copy the code into the sign-in page, or click the button below.</p>
        <div class="cta">
          <a href="${loginUrl}" class="btn">${eventId ? 'Sign in & RSVP' : 'Sign in'}</a>
        </div>
        <p class="muted">If you didn't request this code, you can ignore this email. No changes will be made to your account.</p>
      </div>
    </div>
    <div class="foot">
      <p style="margin: 0;"><a href="${origin}">Gatherly</a>. The simple way to organize gatherings.</p>
    </div>
  </div>
</body>
</html>
  `;

  const emailData = {
    to: [
      {
        email: recipientEmail,
        name: recipientName || 'Gatherly User'
      }
    ],
    from: {
      email: 'no-reply@gatherly.org',
      name: 'Gatherly'
    },
    subject: `Your Gatherly code: ${code}`,
    html: html
  };

  console.log('Auth code email data being sent:', JSON.stringify({ ...emailData, code: '***' }, null, 2));

  return sendEmail(emailData);
}

// Keep the sendTestEmail function for testing purposes
export async function sendTestEmail() {
  return sendWelcomeEmail('joshjmanning@gmail.com', 'Josh Manning');
}
